import { createSelector } from '@reduxjs/toolkit';
import ms from 'ms';

import { GlobalState } from '@/redux/store';
import { emptySellerCatalogCounts } from '@/types/SellerCatalogCounts';

export const SELLER_CATALOG_COUNTS_CACHE_TIME = ms('30m');

const stateSelector = (state: GlobalState) => state;
const sellerCatalogCountsSlice = createSelector(stateSelector, (state) => state.sellerCatalogCounts);

const idSelector = (_: GlobalState, id: number) => id;
const idsSelector = (_: GlobalState, ids: number[]) => ids;

const byIdSelector = createSelector(sellerCatalogCountsSlice, (state) => state.byId);

const loadedSelector = createSelector(sellerCatalogCountsSlice, (state) => state.loaded);

const loadingSelector = createSelector(sellerCatalogCountsSlice, (state) => state.loading);

export const getSellerCatalogCounts = createSelector([byIdSelector, idSelector], (byId, id) => {
    return byId[id] || emptySellerCatalogCounts;
});

export const getSellersCatalogCounts = createSelector([byIdSelector, idsSelector], (byId, ids) =>
    ids.map(
        (id) =>
            byId[id] || {
                doneCount: 0,
                liveCount: 0,
                onlineCount: 0,
                sellerId: id,
                upcomingCount: 0,
            }
    )
);

export const getSellerUpcomingCatalogCount = createSelector(
    getSellerCatalogCounts,
    ({ upcomingCount }) => upcomingCount || 0
);

export const getSellerCatalogDoneCount = createSelector(getSellerCatalogCounts, ({ doneCount }) => doneCount || 0);

// takes in a (state, id) and returns load time
const getLoadTimeForSellerCatalogCounts = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

// takes in a (state, id) and returns if data is loading
const isSellerCatalogCountsLoading = createSelector(
    [loadingSelector, idSelector],
    (loading, id) => loading.indexOf(id) !== -1
);

const shouldFetchSellerCatalogCounts = (state: GlobalState, sellerId: number) => {
    if (!sellerId) {
        return false;
    }
    const item = getSellerCatalogCounts(state, sellerId);
    if (item) {
        const loaded = getLoadTimeForSellerCatalogCounts(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < SELLER_CATALOG_COUNTS_CACHE_TIME) {
            return false;
        }
    }
    const loading = isSellerCatalogCountsLoading(state, sellerId);
    return !loading;
};

export const whichSellerCatalogCountsNeeded = (state: GlobalState, sellerIds: number[]) => {
    return sellerIds.filter((sellerId) => shouldFetchSellerCatalogCounts(state, sellerId));
};
